import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { RefreshCw, FolderIcon, AlertCircle, Grid3X3, Kanban } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { ProjectsKanbanBoard } from '@/components/projects-kanban-board';
import { getStatusColors } from '@/lib/status-colors';

// In-memory cache for projects data
const projectsCache = new Map<string, { data: ProjectsResponse; timestamp: number }>()
const PROJECTS_CACHE_DURATION = 60 * 60 * 1000 // 60 minutes (1 hour)
const PROJECTS_CACHE_KEY = 'projects_data'

interface Project {
  id: string;
  name: string;
  status?: string;
  description?: string;
  created_time?: string;
  last_edited_time?: string;
  url?: string;
  task_count?: number;
  budget?: number;
  properties?: Record<string, any>;
}

interface ProjectsResponse {
  projects?: Project[];
  meta?: {
    total: number;
    count: number;
  };
  _debug?: Record<string, any>;
}

export default function ProjectsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("projects");
  const [projectViewMode, setProjectViewMode] = useState<"kanban" | "grid">("kanban");

  const fetchProjects = async (force: boolean = false) => {
    try {
      // Check in-memory cache first if not forcing refresh
      if (!force) {
        const cached = projectsCache.get(PROJECTS_CACHE_KEY);
        if (cached && (Date.now() - cached.timestamp) < PROJECTS_CACHE_DURATION) {
          const wp = (window as any).WP_COCKPIT;
          const isDev = wp?.devMode || false;
          if (isDev) console.log('[fetchProjects] Returning cached data');
          
          if (cached.data.projects && Array.isArray(cached.data.projects)) {
            setProjects(cached.data.projects);
            setMeta(cached.data.meta || null);
          } else {
            setProjects([]);
            setMeta(null);
          }
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }

      if (force) {
        // Clear cache for hard refresh
        projectsCache.delete(PROJECTS_CACHE_KEY);
        const wp = (window as any).WP_COCKPIT;
        const isDev = wp?.devMode || false;
        if (isDev) console.log('[fetchProjects] Cache cleared for hard refresh');
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const params = new URLSearchParams();
      if (force) {
        params.append('force', '1');
        // Add cache buster to prevent browser/CDN caching
        params.append('_t', Date.now().toString());
      }

      const url = `/wp-json/kj-cockpit/v1/fetch-projects?${params.toString()}`;
      
      const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      };
      
      // Add WP REST nonce header if provided by server-localized object
      try {
        const wp = (window as any).WP_COCKPIT;
        const isDev = wp?.devMode || false;
        if (isDev) {
          console.log('[fetchProjects] Force refresh:', force);
          console.log('[fetchProjects] URL:', url);
          console.log('[fetchProjects] WP_COCKPIT object:', wp);
        }
        if (wp && wp.restNonce) {
          headers['X-WP-Nonce'] = String(wp.restNonce);
          if (isDev) console.log('[fetchProjects] Added nonce to headers:', headers['X-WP-Nonce']);
        } else {
          console.error('No restNonce found in WP_COCKPIT');
        }
      } catch (e) {
        console.error('Error accessing WP_COCKPIT:', e);
      }
      
      const fetchOptions: RequestInit = { 
        credentials: 'same-origin', 
        headers 
      };
      if (force) {
        fetchOptions.cache = 'no-store';
      }
      
      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ProjectsResponse = await response.json();
      
      // Cache the response
      projectsCache.set(PROJECTS_CACHE_KEY, {
        data: data,
        timestamp: Date.now()
      });
      
      const wp = (window as any).WP_COCKPIT;
      const isDev = wp?.devMode || false;
      if (isDev) console.log('[fetchProjects] Cached response data');
      
      if (data.projects && Array.isArray(data.projects)) {
        setProjects(data.projects);
        setMeta(data.meta || null);
      } else {
        setProjects([]);
        setMeta(null);
      }
      
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Handle URL parameters
  useEffect(() => {
    const pathname = location.pathname;
    
    // Determine the current tab from pathname
    let currentTab = "projects"; // default
    if (pathname.includes("/projects/tasks")) {
      currentTab = "tasks";
    }
    
    setActiveTab(currentTab);
  }, [location.pathname]);

  // Handle URL parameters for direct project access
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        // Navigate to the project page if we're not already there
        navigate(`/projects/project/${project.id}`, { replace: true });
      }
    }
  }, [projectId, projects, navigate]);

  const handleRefresh = () => {
    fetchProjects(true);
  };

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    if (newTab === "tasks") {
      navigate("/projects/tasks", { replace: true });
    } else {
      navigate("/projects", { replace: true });
    }
  };

  const handleProjectClick = (project: Project) => {
    // Navigate directly to the project page instead of opening a dialog
    navigate(`/projects/project/${project.id}`);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  // Skeleton Loading Component
  const ProjectsSkeleton = () => (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }, (_, i) => (
        <Card key={i} className="animate-pulse">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-4"></div>
            <div className="space-y-2">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
            <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
              <div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse"></div>
              </div>
              <Skeleton className="h-10 w-24" />
            </div>
            
            <div className="p-6">
              <ProjectsSkeleton />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          {/* Header with Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
            <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Projects</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {meta?.total ? `${meta.total} projects` : 'Manage your project portfolio'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button 
                  onClick={handleRefresh} 
                  disabled={refreshing}
                  variant="outline"
                  className="gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              </div>
            </div>
          </div>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <div className="space-y-6">
              {/* Projects View Header with Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Project Portfolio</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {meta?.total ? `${meta.total} projects` : 'Manage your project portfolio'}
                  </p>
                </div>
                
                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                  <Button
                    variant={projectViewMode === "kanban" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setProjectViewMode("kanban")}
                    className="h-8 px-3"
                  >
                    <Kanban className="h-4 w-4 mr-1" />
                    Kanban
                  </Button>
                  <Button
                    variant={projectViewMode === "grid" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setProjectViewMode("grid")}
                    className="h-8 px-3"
                  >
                    <Grid3X3 className="h-4 w-4 mr-1" />
                    Grid
                  </Button>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {refreshing && !loading && (
                <ProjectsSkeleton />
              )}

              {!refreshing && projects.length === 0 && !error && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FolderIcon className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">No Projects Found</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      No projects are currently available for your account.
                    </p>
                    <Button onClick={handleRefresh} variant="outline">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Projects
                    </Button>
                  </CardContent>
                </Card>
              )}

              {!refreshing && projects.length > 0 && (
                <>
                  {projectViewMode === "kanban" ? (
                    <ProjectsKanbanBoard 
                      projects={projects}
                      onSelectProject={handleProjectClick}
                    />
                  ) : (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {projects.map((project) => (
                        <Card 
                          key={project.id} 
                          className="border-l-4 hover:shadow-lg transition-shadow cursor-pointer"
                          style={{ borderLeftColor: getStatusColors(project.status || '').borderColorValue }}
                          onClick={() => handleProjectClick(project)}
                        >
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-3">
                              <h3 className="font-semibold text-lg leading-tight flex-1 mr-2 text-gray-900 dark:text-gray-100">
                                {project.name}
                              </h3>
                              {project.status && (
                                <Badge 
                                  className={`shrink-0 ${getStatusColors(project.status).bg} ${getStatusColors(project.status).text} ${getStatusColors(project.status).border} border`}
                                >
                                  {project.status}
                                </Badge>
                              )}
                            </div>
                            
                            {project.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                                {project.description}
                              </p>
                            )}
                            
                            {(project.task_count !== undefined || project.budget !== undefined) && (
                              <div className="flex items-center justify-between gap-2 mb-3">
                                {project.task_count !== undefined && (
                                  <Badge variant="outline" className="text-xs">
                                    {project.task_count} {project.task_count === 1 ? 'task' : 'tasks'}
                                  </Badge>
                                )}
                                {project.budget !== undefined && (
                                  <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                                    ${project.budget.toLocaleString()}
                                  </Badge>
                                )}
                              </div>
                            )}
                            
                            <div className="space-y-2 text-xs text-gray-500 dark:text-gray-500">
                              {project.created_time && (
                                <div>Created: {formatDate(project.created_time)}</div>
                              )}
                              {project.last_edited_time && (
                                <div>Updated: {formatDate(project.last_edited_time)}</div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {refreshing && !loading && (
          <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg p-3 flex items-center space-x-2">
            <Spinner className="h-4 w-4" />
            <span className="text-sm text-gray-900 dark:text-gray-100">Refreshing...</span>
          </div>
        )}
      </main>
    </div>
  );
}